import { useEffect, useCallback } from 'react';
import { useBoardBackground } from './useBoardBackground';
import {
  hexToHSL,
  HSLToHex,
  N10,
  getHSLNumbersFromHSLString,
  getHSLFunctionArgs,
} from '@trello/colors';
import { hasValidContrastRatio } from '@trello/a11y';
import {
  TextLightColor,
  TextDefaultColor,
  IconLightColor,
  IconDefaultColor,
} from '@trello/nachos/tokens';
import { sendErrorEvent } from '@trello/error-reporting';
import {
  THEME_DARK,
  THEME_LIGHT,
  lightTheme,
  useThemeVariables,
} from './useThemeVariables';

export const BACKGROUND_BRIGHTNESS_DARK = 'dark';
export const BACKGROUND_BRIGHTNESS_LIGHT = 'light';
export const BACKGROUND_BRIGHTNESS_UNKNOWN = 'unknown';
const TEXT_TRANSPARENCY = 0.16;
const BACKGROUND_TRANSPARENCY = 0.9;
export const OPACITY_DARK_1 = 'hsla(0,0%,0%,0.16)';

export function useTheme(): null {
  const [themeVariables, setThemeVariables] = useThemeVariables();

  const {
    backgroundBrightness,
    backgroundTopColor,
    backgroundColor,
  } = useBoardBackground();

  const getBackgroundWithValidContrast = useCallback(
    (dynamicTextColor, backgroundTopColor) => {
      const contrastRatioLevelAA = 4.5;
      const contrastRatioLevelAAA = 7;
      const contrastRatioBetweenAAandAAA =
        (contrastRatioLevelAA + contrastRatioLevelAAA) / 2;

      // convert to HSL
      let backgroundTopColorHSL = hexToHSL(backgroundTopColor);

      // only check contrast for image backgrounds (not solid / gradient bgs)
      if (!backgroundColor && backgroundTopColor) {
        const hasValidContrast = hasValidContrastRatio(
          backgroundTopColor,
          dynamicTextColor,
          14,
          false,
          contrastRatioBetweenAAandAAA,
        );

        if (!hasValidContrast.isValid && hasValidContrast.contrastRatio) {
          let isNotValidContrast = true;
          let count = 0;
          // for best performance, only do this 10 times or until lightness is at 0% or 100%
          while (isNotValidContrast && count < 10) {
            count++;

            // get the parts of HSL so can adjust lightness (L)
            const hslNumbers = getHSLNumbersFromHSLString(
              backgroundTopColorHSL,
            );
            const [h, s, l] = hslNumbers;

            let newLightness = l;
            if (backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK) {
              // decrease lightness value by 20%
              newLightness = l - l * 0.2;
              if (newLightness < 0) {
                // lightness below zero is meaningless so set to 0% and break out of the loop
                // multiply saturation by 100 for percentages
                backgroundTopColorHSL = `hsl(${h},${(s * 100).toFixed(1)}%,0%)`;
                break;
              }
            }

            if (backgroundBrightness === BACKGROUND_BRIGHTNESS_LIGHT) {
              // increase lightness value by 20%
              newLightness = l + l * 0.2;
              if (newLightness > 1) {
                // lightness above 1 is meaningless so set to 100% and break out of the loop
                backgroundTopColorHSL = `hsl(${h},${(s * 100).toFixed(
                  1,
                )}%,100%)`;
                break;
              }
            }

            // Multiply lightness and saturation by 100 for percentages
            const saturationPercentage = +(s * 100).toFixed(1);
            const lightnessPercentage = newLightness
              ? +(newLightness * 100).toFixed(1)
              : l;
            backgroundTopColorHSL = `hsl(${h},${saturationPercentage}%,${lightnessPercentage}%)`;

            const hexToCheck = HSLToHex(h, s, newLightness);

            const hasValidContrast = hasValidContrastRatio(
              hexToCheck,
              dynamicTextColor,
              14,
              false,
              contrastRatioBetweenAAandAAA,
            );

            if (hasValidContrast.isValid && hasValidContrast.contrastRatio) {
              // valid contrast found, exit loop
              isNotValidContrast = false;
            }
          }
        }
      }

      return backgroundTopColorHSL;
    },
    [backgroundBrightness, backgroundColor],
  );

  useEffect((): void => {
    try {
      // determine dynamic text color
      const dynamicTextColor =
        backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK
          ? TextLightColor
          : TextDefaultColor;
      const dynamicTextColorTransparent = hexToHSL(
        dynamicTextColor,
        TEXT_TRANSPARENCY,
      );

      // determine dynamic icon color
      const dynamicIconColor =
        backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK
          ? IconLightColor
          : IconDefaultColor;

      // determine dynamic background color
      let dynamicBackgroundColorTransparent = N10;
      let dynamicBackgroundColor = N10;
      // only do these calculations for image backgrounds
      if (!backgroundColor && backgroundTopColor) {
        // ensure background color meets WCAG AA contrast ratio
        const backgroundTopColorHSL = getBackgroundWithValidContrast(
          dynamicTextColor,
          backgroundTopColor,
        );
        const hueSaturationLightness = getHSLFunctionArgs(
          backgroundTopColorHSL,
        );
        dynamicBackgroundColorTransparent = `hsla(${hueSaturationLightness},${BACKGROUND_TRANSPARENCY})`;
        dynamicBackgroundColor = `hsl(${hueSaturationLightness})`;
      }

      // Pass in a setter fn so that TypeScript doesn't treat this as a partial.
      setThemeVariables(() => ({
        theme:
          backgroundBrightness === BACKGROUND_BRIGHTNESS_DARK
            ? THEME_DARK
            : THEME_LIGHT,
        // for solid color or gradient backgrounds always use a dark opacity background
        // for image backgrounds set this dynamically
        dynamicBackgroundColor: backgroundColor
          ? OPACITY_DARK_1
          : dynamicBackgroundColor,
        dynamicBackgroundColorTransparent: backgroundColor
          ? OPACITY_DARK_1
          : dynamicBackgroundColorTransparent,
        dynamicIconColor,
        dynamicTextColor,
        dynamicTextColorTransparent,
      }));
    } catch (e) {
      // if something fails while doing these computations, default to a light theme
      setThemeVariables(lightTheme);

      sendErrorEvent(e, {
        tags: {
          ownershipArea: 'trello-teamplates',
          feature: 'Theme',
        },
      });
    }
  }, [
    backgroundBrightness,
    backgroundTopColor,
    backgroundColor,
    setThemeVariables,
    getBackgroundWithValidContrast,
  ]);

  // Listen for changes in themeVariables and assign them to the root node.
  useEffect(() => {
    // attach global CSS Variables to the document so
    // that even React portalled content can access
    // (e.g. Nachos Popover, some Atlaskit components)
    const root: HTMLElement = document.documentElement;

    if (!root) {
      return;
    }

    const {
      theme,
      dynamicBackgroundColor,
      dynamicBackgroundColorTransparent = null,
      dynamicIconColor,
      dynamicTextColor,
      dynamicTextColorTransparent,
    } = themeVariables;

    ([THEME_DARK, THEME_LIGHT] as const).forEach((className) => {
      root.classList.toggle(className, theme === className);
    });

    ([
      ['--dynamic-background-color', dynamicBackgroundColor],
      [
        '--dynamic-background-color-transparent',
        dynamicBackgroundColorTransparent,
      ],
      ['--dynamic-icon-color', dynamicIconColor],
      ['--dynamic-text-color', dynamicTextColor],
      ['--dynamic-text-color-transparent', dynamicTextColorTransparent],
    ] as const).forEach(([key, value]) => root.style.setProperty(key, value));

    // Theme is set wholesale in this hook, so we don't need to worry about
    // targeting individual dependencies.
  }, [themeVariables]);

  return null;
}
